import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { UIPRWithTypedCoverage } from '../../../../../../../api/teamMetricsApi';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  titleText: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  subtitle: {
    fontSize: 14,
    color: '#8E9299',
  },
  divider: {
    width: 1,
    height: 32,
    backgroundColor: '#E2E8F0',
  },
  tableContainer: {
    border: '1px solid #E2E8F0',
    borderRadius: 4,
  },
  tableHead: {
    backgroundColor: '#F7FAFC',
  },
  headerCell: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    borderBottom: '1px solid #E2E8F0',
    whiteSpace: 'nowrap',
  },
  cell: {
    borderBottom: '1px solid #E2E8F0',
    verticalAlign: 'top',
    padding: theme.spacing(1.5, 2),
  },
  prLink: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    color: '#0070F3',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  externalIcon: {
    fontSize: 14,
    marginTop: 2,
    opacity: 0.7,
  },
  prTitle: {
    maxWidth: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  authorCell: {
    fontSize: 13,
  },
  authorLink: {
    color: '#0070F3',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  authorText: {
    color: '#4A5568',
  },
  dateCell: {
    fontSize: 13,
    color: '#718096',
    whiteSpace: 'nowrap',
  },
  changesCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: 10,
    fontWeight: 700,
  },
  additions: {
    color: '#38A169',
  },
  deletions: {
    color: '#E53E3E',
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: '#8E9299',
  },
  filterSection: {
    marginBottom: theme.spacing(3),
  },
}));

interface FrontendPRTableProps {
  prs: UIPRWithTypedCoverage[];
  title: string;
  subtitle: string;
  onBack: () => void;
  onAuthorClick?: (weaveEmail: string) => void;
  dateRangePicker?: React.ReactNode;
  filterSection?: React.ReactNode;
}

export const FrontendPRTable: React.FC<FrontendPRTableProps> = ({
  prs,
  title,
  subtitle,
  onBack,
  onAuthorClick,
  dateRangePicker,
  filterSection,
}) => {
  const classes = useStyles();

  const sortedPrs = useMemo(() => {
    return [...prs].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
  }, [prs]);

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.headerTop}>
          <Box className={classes.titleSection}>
            <IconButton onClick={onBack} className={classes.backButton}>
              <ArrowBackIcon />
            </IconButton>
            <Box className={classes.titleText}>
              <Typography className={classes.title}>{title}</Typography>
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            </Box>
          </Box>
          {dateRangePicker}
        </Box>
      </Box>

      {filterSection && <Box className={classes.filterSection}>{filterSection}</Box>}

      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.headerCell}>Pull Request</TableCell>
              <TableCell className={classes.headerCell}>Changes</TableCell>
              <TableCell className={classes.headerCell}>Author</TableCell>
              <TableCell className={classes.headerCell} align="right">Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPrs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className={classes.emptyState}>
                  No pull requests found for the selected period
                </TableCell>
              </TableRow>
            ) : (
              sortedPrs.map((pr) => (
                <TableRow key={pr.id}>
                  <TableCell className={classes.cell}>
                    <a
                      href={pr.prUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.prLink}
                    >
                      <Tooltip title={pr.title}>
                        <span className={classes.prTitle}>{pr.title}</span>
                      </Tooltip>
                      <OpenInNewIcon className={classes.externalIcon} />
                    </a>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Box className={classes.changesCell}>
                      <span className={classes.additions}>+{pr.additions}</span>
                      <span className={classes.deletions}>-{pr.deletions}</span>
                    </Box>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.authorCell}`}>
                    {pr.weaveUser && onAuthorClick ? (
                      <span
                        className={classes.authorLink}
                        onClick={() => onAuthorClick(pr.weaveUser!)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && onAuthorClick(pr.weaveUser!)}
                      >
                        {pr.weaveUser}
                      </span>
                    ) : (
                      <span className={classes.authorText}>{pr.weaveUser || pr.author}</span>
                    )}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.dateCell}`} align="right">
                    {pr.date || 'N/A'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FrontendPRTable;
